import { UserFacingError } from "../utils/userFacingError";

/**
 * The Portal API's shared PostIF result contract — PostIfResultCode in
 * PortalAPI.Application/Helpers/Enums.cs. Every PostIF write sproc answers with one of these
 * through @Result, and PostIfController.ToActionResult maps them 1:1 onto HTTP statuses (404 for
 * -1, 422 for -2/-3, 409 for -4/-7, 403 for -5, 423 for -6) — so a negative code always arrives as
 * a REJECTED axios promise, never as data.
 */
export const POSTIF_RESULT_CODE = {
  SUCCESS: 1,
  UNHANDLED_ERROR: 0,
  NOT_FOUND: -1,
  VALIDATION_FAILED: -2,
  MISSING_REQUIRED: -3,
  WRONG_STATE: -4,
  FORBIDDEN: -5,
  LOCKED: -6,
  APPROVAL_CHAIN_NOT_CONFIGURED: -7,
} as const;

/**
 * What the person on the screen is shown for each failing code, through useApi's central alert.
 *
 * Each sentence has to answer "what now?" on its own — these land with no surrounding context.
 * -4 is the one that prompted this: re-submitting a returned PostIF whose status had moved on
 * showed axios's own "Request failed with status code 409", which says nothing about the form.
 */
const MESSAGE_BY_CODE: Record<number, string> = {
  [POSTIF_RESULT_CODE.UNHANDLED_ERROR]:
    "Something went wrong on the server and nothing was saved. Try again if it keeps happening, send this to support.",
  [POSTIF_RESULT_CODE.NOT_FOUND]: "This Post Interview Form no longer exists it may have been deleted.",
  [POSTIF_RESULT_CODE.VALIDATION_FAILED]:
    "The server refused one of the answers. Check the highlighted field and try again.",
  [POSTIF_RESULT_CODE.MISSING_REQUIRED]: "Some required answers are still missing.",
  [POSTIF_RESULT_CODE.WRONG_STATE]:
    "This form is no longer open for changes it has been submitted or decided since this page was opened. Reload it to see where it stands.",
  [POSTIF_RESULT_CODE.FORBIDDEN]: "You aren't allowed to do that on this Post Interview Form.",
  [POSTIF_RESULT_CODE.LOCKED]: "Someone else is working on this form right now. Try again in a few minutes.",
  [POSTIF_RESULT_CODE.APPROVAL_CHAIN_NOT_CONFIGURED]:
    "The approval chain isn't set up the Department, HR or Executive Director couldn't be resolved, so nothing was submitted. Ask an administrator to check the PostIF settings, then submit again.",
};

/**
 * The shape of a rejected apiClient call, read structurally rather than through
 * axios.isAxiosError: importing axios here would pull its ESM entry point into every Jest suite
 * that reaches this module, and CRA's Jest does not transform node_modules (the suites mock
 * utils/apiClient precisely so axios never loads).
 */
interface HttpErrorLike {
  response?: { status?: number; data?: unknown };
}

/**
 * Unwraps a PostIF write, turning the API's business result into either data the caller can
 * inspect or an Error whose message is fit to put in front of the person on the screen.
 *
 * Without this every negative code is just an axios rejection: useApi alerts "Request failed with
 * status code 409" and the DTO in the body — its code, and the `missing` / `rejected` field lists —
 * is dropped. That also left the review drawer's missing-answers list and the Position/Pay cards'
 * per-field rejection notes as dead code, even though both endpoints are documented as returning
 * them.
 *
 * `resolveCodes` is the opt-in for the codes a particular caller RENDERS itself (there, the
 * per-field payload is the answer, not a failure). Deliberately per call site rather than global:
 * -3 means "required answers are missing, here they are" on submit but "you didn't type a reason"
 * on reject, and the latter must stay an alert rather than resolve as if the send-back had gone
 * through.
 */
export const readPostIfWrite = async <T extends { code: number }>(
  call: () => Promise<{ data: T }>,
  resolveCodes: readonly number[] = [],
): Promise<T> => {
  try {
    const response = await call();
    return response.data;
  } catch (error) {
    const response = (error as HttpErrorLike | null)?.response;
    // No HTTP response means it never got that far — apiClient's interceptor already normalized
    // network failures into a UserFacingError carrying a user-ready message, so leave it alone.
    if (!response) throw error;

    const data = response.data as T | undefined;
    const code = typeof data?.code === "number" ? data.code : null;

    if (code !== null && resolveCodes.includes(code)) return data as T;

    // Forbid() answers 403 with no body at all, so -5 has to be recognised by its status.
    const message =
      code !== null
        ? MESSAGE_BY_CODE[code]
        : response.status === 403
          ? MESSAGE_BY_CODE[POSTIF_RESULT_CODE.FORBIDDEN]
          : undefined;

    // UserFacingError (Bug 79203) is what marks these as fit to show: useApi replaces any
    // unmarked error with a generic sentence, so a plain Error here would silently downgrade
    // every PostIF result message back to the "Request failed with status code 409" problem
    // this table exists to solve.
    throw message ? new UserFacingError(message) : error;
  }
};
